import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Plus, Edit, Trash2, Save, X, Check, MapPin, ChevronDown, ChevronRight } from 'lucide-react';
import LocationMap from '../components/LocationMap';

interface Kunde {
  id: string;
  name: string;
  kundennummer: string | null;
  type: 'ohne_kostenstelle' | 'sap_nummer' | 'kostenstelle';
  types?: string[];
}

interface Standort {
  id: string;
  name: string;
  adresse: string;
  strasse: string;
  plz: string;
  stadt: string;
  kunde_id: string;
  standortnummer: string | null;
}

const CUSTOMER_TYPES = [
  { id: 'ohne_kostenstelle', label: 'Ohne Kostenstelle' },
  { id: 'sap_nummer', label: 'SAP-Nummer' },
  { id: 'kostenstelle', label: 'Kostenstelle' }
];

function KundenVerwaltung() {
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [standorte, setStandorte] = useState<Standort[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showTypePopup, setShowTypePopup] = useState(false);
  const [activeTab, setActiveTab] = useState<'kunden' | 'standorte'>('kunden');
  const [expandedKunde, setExpandedKunde] = useState<string | null>(null);
  const [expandedMap, setExpandedMap] = useState<string | null>(null);
  
  // Kunden form state
  const [kundeFormData, setKundeFormData] = useState<Omit<Kunde, 'id'>>({
    name: '',
    kundennummer: '',
    type: 'ohne_kostenstelle',
    types: ['ohne_kostenstelle']
  });

  // Standort form state
  const [standortFormData, setStandortFormData] = useState<Omit<Standort, 'id' | 'adresse'>>({
    name: '',
    strasse: '',
    plz: '',
    stadt: '',
    kunde_id: '',
    standortnummer: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      const [kundenRes, standorteRes] = await Promise.all([
        supabase.from('kunden').select('*').order('name'),
        supabase.from('standorte').select('*').order('name')
      ]);

      if (kundenRes.error) throw kundenRes.error;
      if (standorteRes.error) throw standorteRes.error;
      
      setKunden(kundenRes.data || []);
      setStandorte(standorteRes.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      alert('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }

  // Kunden handlers
  const handleKundeInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setKundeFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTypeToggle = (typeId: string) => {
    setKundeFormData((prev) => {
      const currentTypes = prev.types || [];
      let newTypes: string[];
      
      if (currentTypes.includes(typeId)) {
        // Don't allow removing the last type
        if (currentTypes.length === 1) {
          return prev;
        }
        // Remove the type if it's already selected
        newTypes = currentTypes.filter(t => t !== typeId);
      } else {
        // Add the type if it's not already selected
        newTypes = [...currentTypes, typeId];
      }
      
      // Update the primary type if needed
      const newType = newTypes.length > 0 ? newTypes[0] as any : 'ohne_kostenstelle';
      
      return {
        ...prev,
        types: newTypes,
        type: newType
      };
    });
  };

  const closeTypePopup = () => {
    setShowTypePopup(false);
  };

  const handleKundeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        // Update existing customer
        const { error } = await supabase
          .from('kunden')
          .update(kundeFormData)
          .eq('id', editingId);

        if (error) throw error;
        
        setKunden(prev => 
          prev.map(kunde => 
            kunde.id === editingId ? { ...kunde, ...kundeFormData } : kunde
          )
        );
        setEditingId(null);
      } else {
        // Create new customer
        const { data, error } = await supabase
          .from('kunden')
          .insert([kundeFormData])
          .select()
          .single();

        if (error) throw error;
        setKunden(prev => [...prev, data]);
      }
      
      // Reset form
      setKundeFormData({
        name: '',
        kundennummer: '',
        type: 'ohne_kostenstelle',
        types: ['ohne_kostenstelle']
      });
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern des Kunden');
    }
  };

  const startEditingKunde = (kunde: Kunde) => {
    setEditingId(kunde.id);
    setKundeFormData({
      name: kunde.name,
      kundennummer: kunde.kundennummer || '',
      type: kunde.type,
      types: kunde.types || [kunde.type]
    });
    setActiveTab('kunden');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setKundeFormData({
      name: '',
      kundennummer: '',
      type: 'ohne_kostenstelle',
      types: ['ohne_kostenstelle']
    });
  };

  const deleteKunde = async (id: string) => {
    if (!confirm('Sind Sie sicher, dass Sie diesen Kunden löschen möchten?')) {
      return;
    }
    
    try {
      // Check if customer has locations
      const { data: kundenStandorte, error: standorteError } = await supabase
        .from('standorte')
        .select('id')
        .eq('kunde_id', id);
        
      if (standorteError) throw standorteError;
      
      if (kundenStandorte && kundenStandorte.length > 0) {
        alert('Dieser Kunde hat zugeordnete Standorte und kann nicht gelöscht werden.');
        return;
      }
      
      // Check if customer has orders
      const { data: auftraege, error: auftraegeError } = await supabase
        .from('auftraege')
        .select('id')
        .eq('kunde_id', id);
        
      if (auftraegeError) throw auftraegeError;
      
      if (auftraege && auftraege.length > 0) {
        alert('Dieser Kunde hat zugeordnete Aufträge und kann nicht gelöscht werden.');
        return;
      }
      
      const { error } = await supabase
        .from('kunden')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setKunden(prev => prev.filter(kunde => kunde.id !== id));
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen des Kunden');
    }
  };

  // Standorte handlers
  const handleStandortInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setStandortFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStandortSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Combine address fields for the adresse field
      const adresse = `${standortFormData.strasse}, ${standortFormData.plz} ${standortFormData.stadt}`.trim();
      
      if (editingId) {
        // Update existing location
        const { error } = await supabase
          .from('standorte')
          .update({
            ...standortFormData,
            adresse
          })
          .eq('id', editingId);

        if (error) throw error;
        
        setStandorte(prev => 
          prev.map(standort => 
            standort.id === editingId ? { 
              ...standort, 
              ...standortFormData,
              adresse
            } : standort
          )
        );
        setEditingId(null);
      } else {
        // Create new location
        const { data, error } = await supabase
          .from('standorte')
          .insert([{
            ...standortFormData,
            adresse
          }])
          .select()
          .single();

        if (error) throw error;
        setStandorte(prev => [...prev, data]);
      }
      
      // Reset form
      setStandortFormData({
        name: '',
        strasse: '',
        plz: '',
        stadt: '',
        kunde_id: '',
        standortnummer: '',
      });
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern des Standorts');
    }
  };

  const startEditingStandort = (standort: Standort) => {
    setEditingId(standort.id);
    
    // Extract address components if they're not already set
    let strasse = standort.strasse || '';
    let plz = standort.plz || '';
    let stadt = standort.stadt || '';
    
    // If the new fields are empty but we have an address, try to parse it
    if ((!strasse || !plz || !stadt) && standort.adresse) {
      const addressParts = standort.adresse.split(',');
      if (addressParts.length > 1) {
        // Format: "Street, Postal City"
        strasse = addressParts[0].trim();
        const cityPart = addressParts[1].trim();
        
        // Try to extract postal code (assuming it's 5 digits)
        const postalMatch = cityPart.match(/\b\d{5}\b/);
        if (postalMatch) {
          plz = postalMatch[0];
          stadt = cityPart.replace(plz, '').trim();
        } else {
          stadt = cityPart;
        }
      } else {
        // No comma, just use the whole address as street
        strasse = standort.adresse;
      }
    }
    
    setStandortFormData({
      name: standort.name,
      strasse,
      plz,
      stadt,
      kunde_id: standort.kunde_id,
      standortnummer: standort.standortnummer || '',
    });
    
    setActiveTab('standorte');
  };

  const deleteStandort = async (id: string) => {
    if (!confirm('Sind Sie sicher, dass Sie diesen Standort löschen möchten?')) {
      return;
    }
    
    try {
      // Check if location has orders
      const { data: auftraege, error: auftraegeError } = await supabase
        .from('auftraege')
        .select('id')
        .eq('standort_id', id);
        
      if (auftraegeError) throw auftraegeError;
      
      if (auftraege && auftraege.length > 0) {
        alert('Dieser Standort hat zugeordnete Aufträge und kann nicht gelöscht werden.');
        return;
      }
      
      const { error } = await supabase
        .from('standorte')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setStandorte(prev => prev.filter(standort => standort.id !== id));
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen des Standorts');
    }
  };

  const toggleKundeExpand = (kundeId: string) => {
    setExpandedKunde(expandedKunde === kundeId ? null : kundeId);
  };

  const toggleMap = (standortId: string) => {
    setExpandedMap(expandedMap === standortId ? null : standortId);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'ohne_kostenstelle':
        return 'Ohne Kostenstelle';
      case 'sap_nummer':
        return 'SAP-Nummer';
      case 'kostenstelle':
        return 'Kostenstelle';
      default:
        return type;
    }
  };

  const getTypesDisplay = (types: string[] | undefined) => {
    if (!types || types.length === 0) {
      return 'Ohne Kostenstelle';
    }
    
    return types.map(type => getTypeLabel(type)).join(', ');
  };

  const getKundenStandorte = (kundeId: string) => {
    return standorte.filter(standort => standort.kunde_id === kundeId);
  };

  // Helper function to format the full address
  const formatAddress = (standort: Standort) => {
    // If we have the new fields, use them
    if (standort.strasse || standort.plz || standort.stadt) {
      const parts = [];
      if (standort.strasse) parts.push(standort.strasse);
      if (standort.plz || standort.stadt) {
        const cityPart = [standort.plz, standort.stadt].filter(Boolean).join(' ');
        if (cityPart) parts.push(cityPart);
      }
      return parts.join(', ');
    }
    
    // Fall back to the original address field
    return standort.adresse;
  };

  if (loading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Kundenverwaltung</h1>
        
        {/* Tabs */}
        <div className="flex border-b mb-6">
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === 'kunden'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('kunden')}
          >
            Kunden
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === 'standorte'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('standorte')}
          >
            Standorte
          </button>
        </div>
        
        {/* Kunden Tab */}
        {activeTab === 'kunden' && (
          <>
            <form onSubmit={handleKundeSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700">Kundenname</label>
                <input
                  type="text"
                  name="name"
                  value={kundeFormData.name}
                  onChange={handleKundeInputChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Firmenname eingeben"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Kundennummer</label>
                <input
                  type="text"
                  name="kundennummer"
                  value={kundeFormData.kundennummer || ''}
                  onChange={handleKundeInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Kundennummer eingeben"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Auftragstypen</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowTypePopup(true)}
                    className="mt-1 block w-full text-left rounded-md border border-gray-300 shadow-sm px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {getTypesDisplay(kundeFormData.types)}
                  </button>
                  
                  {showTypePopup && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={closeTypePopup}
                      ></div>
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                        <div className="p-2">
                          <div className="mb-2 text-sm font-medium text-gray-700 border-b pb-2">
                            Wählen Sie einen oder mehrere Auftragstypen:
                          </div>
                          
                          {CUSTOMER_TYPES.map(type => (
                            <div 
                              key={type.id}
                              className="flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer"
                              onClick={() => handleTypeToggle(type.id)}
                            >
                              <div className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded mr-2">
                                {kundeFormData.types?.includes(type.id) && <Check size={16} className="text-blue-600" />}
                              </div>
                              <span>{type.label}</span>
                            </div>
                          ))}
                          
                          <div className="mt-3 flex justify-end">
                            <button
                              type="button"
                              onClick={closeTypePopup}
                              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                              Fertig
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              <div className="md:col-span-3 flex justify-end space-x-2">
                {editingId && (
                  <button
                    type="button"
                    onClick={cancelEditing}
                    className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 flex items-center gap-2"
                  >
                    <X size={20} />
                    Abbrechen
                  </button>
                )}
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  {editingId ? (
                    <>
                      <Save size={20} />
                      Aktualisieren
                    </>
                  ) : (
                    <>
                      <Plus size={20} />
                      Kunde hinzufügen
                    </>
                  )}
                </button>
              </div>
            </form>
            
            <div className="space-y-4">
              {kunden.map((kunde) => (
                <div key={kunde.id} className="border rounded-lg overflow-hidden">
                  <div 
                    className={`flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50 ${
                      expandedKunde === kunde.id ? 'bg-blue-50' : 'bg-white'
                    }`}
                    onClick={() => toggleKundeExpand(kunde.id)}
                  >
                    <div className="flex-1">
                      <h3 className="font-medium">{kunde.name}</h3>
                      <div className="text-sm text-gray-500 flex flex-wrap gap-2 mt-1">
                        {kunde.kundennummer && (
                          <span className="bg-gray-100 px-2 py-0.5 rounded">
                            #{kunde.kundennummer}
                          </span>
                        )}
                        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                          {getTypesDisplay(kunde.types)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        {getKundenStandorte(kunde.id).length} Standorte
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditingKunde(kunde);
                        }}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Kunde bearbeiten"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteKunde(kunde.id);
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Kunde löschen"
                      >
                        <Trash2 size={18} />
                      </button>
                      {expandedKunde === kunde.id ? (
                        <ChevronDown size={20} className="text-gray-500" />
                      ) : (
                         <ChevronRight size={20} className="text-gray-500" />
                      )}
                    </div>
                  </div>
                  
                  {expandedKunde === kunde.id && (
                    <div className="border-t p-4 bg-gray-50">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-medium text-gray-700 flex items-center gap-1">
                          <MapPin size={16} />
                          Standorte
                        </h4>
                        <button
                          onClick={() => {
                            setStandortFormData(prev => ({
                              ...prev,
                              kunde_id: kunde.id
                            }));
                            setActiveTab('standorte');
                          }}
                          className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 flex items-center gap-1"
                        >
                          <Plus size={14} />
                          Standort hinzufügen
                        </button>
                      </div>
                      
                      {getKundenStandorte(kunde.id).length > 0 ? (
                        <div className="space-y-3">
                          {getKundenStandorte(kunde.id).map(standort => (
                            <div key={standort.id} className="bg-white border rounded-md p-3">
                              <div className="flex justify-between">
                                <div>
                                  <h5 className="font-medium">{standort.name}</h5>
                                  {standort.standortnummer && (
                                    <p className="text-sm text-gray-500">#{standort.standortnummer}</p>
                                  )}
                                  <p className="text-sm mt-1">{formatAddress(standort)}</p>
                                </div>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => toggleMap(standort.id)}
                                    className="text-blue-600 hover:text-blue-800 p-1"
                                    title="Karte anzeigen/ausblenden"
                                  >
                                    <MapPin size={18} />
                                  </button>
                                  <button
                                    onClick={() => startEditingStandort(standort)}
                                    className="text-blue-600 hover:text-blue-800 p-1"
                                    title="Standort bearbeiten"
                                  >
                                    <Edit size={18} />
                                  </button>
                                  <button
                                    onClick={() => deleteStandort(standort.id)}
                                    className="text-red-600 hover:text-red-800 p-1"
                                    title="Standort löschen"
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </div>
                              </div>
                              
                              {expandedMap === standort.id && (
                                <div className="mt-3">
                                  <LocationMap address={standort.adresse} name={standort.name} />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">Keine Standorte vorhanden</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              {kunden.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Noch keine Kunden vorhanden
                </div>
              )}
            </div>
          </>
        )}
        
        {/* Standorte Tab */}
        {activeTab === 'standorte' && (
          <>
            <form onSubmit={handleStandortSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700">Kunde</label>
                <select
                  name="kunde_id"
                  value={standortFormData.kunde_id}
                  onChange={handleStandortInputChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Bitte Kunde auswählen...</option>
                  {kunden.map((kunde) => (
                    <option key={kunde.id} value={kunde.id}>
                      {kunde.name}{kunde.kundennummer ? ` (${kunde.kundennummer})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Standortname</label>
                <input
                  type="text"
                  name="name"
                  value={standortFormData.name}
                  onChange={handleStandortInputChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="z.B. Hauptwerk, Filiale Nord"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Standortnummer</label>
                <input
                  type="text"
                  name="standortnummer"
                  value={standortFormData.standortnummer || ''}
                  onChange={handleStandortInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="z.B. S001, WE-123"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Kundenspezifische Standortnummer (optional)
                </p>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700">Straße und Hausnummer</label>
                    <input
                      type="text"
                      name="strasse"
                      value={standortFormData.strasse}
                      onChange={handleStandortInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="z.B. Hauptstraße 123"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Postleitzahl</label>
                    <input
                      type="text"
                      name="plz"
                      value={standortFormData.plz}
                      onChange={handleStandortInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="z.B. 12345"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Stadt</label>
                    <input
                      type="text"
                      name="stadt"
                      value={standortFormData.stadt}
                      onChange={handleStandortInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="z.B. Berlin"
                    />
                  </div>
                </div>
              </div>
              
              <div className="md:col-span-2 flex justify-end space-x-2">
                {editingId && (
                  <button
                    type="button"
                    onClick={cancelEditing}
                    className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 flex items-center gap-2"
                  >
                    <X size={20} />
                    Abbrechen
                  </button>
                )}
                <button
                  type="submit"
                  disabled={!standortFormData.kunde_id}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {editingId ? (
                    <>
                      <Save size={20} />
                      Aktualisieren
                    </>
                  ) : (
                    <>
                      <Plus size={20} />
                      Standort hinzufügen
                    </>
                  )}
                </button>
              </div>
            </form>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kunde</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Standort</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Standortnummer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adresse</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Karte</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {standorte.map((standort) => {
                    const kunde = kunden.find(k => k.id === standort.kunde_id);
                    return (
                      <tr key={standort.id} className={editingId === standort.id ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {kunde ? kunde.name : 'Unbekannter Kunde'}
                          {kunde?.kundennummer && ` (${kunde.kundennummer})`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{standort.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{standort.standortnummer || '-'}</td>
                        <td className="px-6 py-4">{formatAddress(standort)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => toggleMap(standort.id)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Karte anzeigen/ausblenden"
                          >
                            <MapPin size={20} />
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => startEditingStandort(standort)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Standort bearbeiten"
                            >
                              <Edit size={20} />
                            </button>
                            <button
                              onClick={() => deleteStandort(standort.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Standort löschen"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {standorte.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Noch keine Standorte vorhanden
                </div>
              )}
            </div>
            
            {/* Map display for standorte */}
            {expandedMap && (
              <div className="mt-4 p-4 border rounded-lg bg-gray-50">
                <h3 className="font-medium mb-2">Standort auf der Karte</h3>
                {(() => {
                  const standort = standorte.find(s => s.id === expandedMap);
                  if (standort) {
                    return (
                      <LocationMap address={standort.adresse} name={standort.name} />
                    );
                  }
                  return null;
                })()}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default KundenVerwaltung;